<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { toast } from 'dashboard/composables/useToast';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['edit']);

const { t } = useI18n();
const store = useStore();

const scheduledMessages = useMapGetter(
  'scheduledMessages/getScheduledMessages'
);
const uiFlags = useMapGetter('scheduledMessages/getUIFlags');

const sortedMessages = computed(() => {
  return [...(scheduledMessages.value || [])].sort((a, b) => {
    return new Date(b.scheduled_at) - new Date(a.scheduled_at);
  });
});

const statusConfig = {
  pending: {
    label: 'SCHEDULED_MESSAGES.STATUS.PENDING',
    class: 'bg-n-amber-3 text-n-amber-11',
    icon: 'i-lucide-clock',
  },
  sent: {
    label: 'SCHEDULED_MESSAGES.STATUS.SENT',
    class: 'bg-n-teal-3 text-n-teal-11',
    icon: 'i-lucide-check',
  },
  failed: {
    label: 'SCHEDULED_MESSAGES.STATUS.FAILED',
    class: 'bg-n-ruby-3 text-n-ruby-11',
    icon: 'i-lucide-x',
  },
  cancelled: {
    label: 'SCHEDULED_MESSAGES.STATUS.CANCELLED',
    class: 'bg-n-slate-3 text-n-slate-10',
    icon: 'i-lucide-ban',
  },
};

const getStatusConfig = status => {
  return statusConfig[status] || statusConfig.pending;
};

// Entrega de verdade (lida da mensagem que nasceu na conversa).
const deliveryConfig = {
  read: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.READ',
    class: 'text-n-teal-11',
    icon: 'i-lucide-check-check',
  },
  delivered: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.DELIVERED',
    class: 'text-n-slate-11',
    icon: 'i-lucide-check-check',
  },
  sent: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.SENT',
    class: 'text-n-slate-11',
    icon: 'i-lucide-check',
  },
  delivering: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.DELIVERING',
    class: 'text-n-slate-10',
    icon: 'i-lucide-clock',
  },
  failed: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.FAILED',
    class: 'text-n-ruby-11',
    icon: 'i-lucide-triangle-alert',
  },
  unknown: {
    label: 'SCHEDULED_MESSAGES.DELIVERY.UNKNOWN',
    class: 'text-n-slate-10',
    icon: 'i-lucide-help-circle',
  },
};

const getDeliveryConfig = msg =>
  deliveryConfig[msg.message_status] || deliveryConfig.unknown;

const formatDateTime = dateStr => {
  const date = new Date(dateStr);
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const cancelMessage = async message => {
  try {
    await store.dispatch('scheduledMessages/cancel', message.id);
    toast.success(t('SCHEDULED_MESSAGES.ALERTS.CANCELLED'));
  } catch {
    toast.error(t('SCHEDULED_MESSAGES.ALERTS.CANCEL_ERROR'));
  }
};

const deleteMessage = async message => {
  try {
    await store.dispatch('scheduledMessages/delete', message.id);
    toast.success(t('SCHEDULED_MESSAGES.ALERTS.DELETED'));
  } catch {
    toast.error(t('SCHEDULED_MESSAGES.ALERTS.DELETE_ERROR'));
  }
};

const fetchMessages = () => {
  store.dispatch('scheduledMessages/getByContact', props.contactId);
};

onMounted(fetchMessages);
</script>

<template>
  <div class="flex flex-col h-full">
    <h4 class="text-sm font-medium text-n-slate-12 mb-3">
      {{ $t('SCHEDULED_MESSAGES.DIALOG.HISTORY_TITLE') }}
    </h4>

    <div
      v-if="uiFlags.isFetching"
      class="flex items-center justify-center py-8"
    >
      <Spinner class="text-n-brand" />
    </div>

    <div
      v-else-if="!sortedMessages.length"
      class="flex items-center justify-center py-8 text-sm text-n-slate-10"
    >
      {{ $t('SCHEDULED_MESSAGES.DIALOG.NO_HISTORY') }}
    </div>

    <div v-else class="flex flex-col gap-2 overflow-y-auto max-h-[340px] pr-1">
      <div
        v-for="msg in sortedMessages"
        :key="msg.id"
        class="flex flex-col gap-1.5 p-3 rounded-lg border border-n-weak bg-n-solid-2"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-xs text-n-slate-10">
            {{ formatDateTime(msg.scheduled_at) }}
          </span>
          <span
            class="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium rounded-md"
            :class="getStatusConfig(msg.status).class"
          >
            <span :class="getStatusConfig(msg.status).icon" class="size-3" />
            {{ $t(getStatusConfig(msg.status).label) }}
          </span>
        </div>

        <p class="mb-0 text-sm text-n-slate-12 line-clamp-2">
          {{ msg.content }}
        </p>

        <div
          class="flex flex-wrap items-center gap-x-2 text-xs text-n-slate-10"
        >
          <span v-if="msg.user">{{ msg.user.name }}</span>
          <span
            v-if="msg.status === 'sent'"
            class="inline-flex items-center gap-1"
            :class="getDeliveryConfig(msg).class"
          >
            <span :class="getDeliveryConfig(msg).icon" class="size-3" />
            {{ $t(getDeliveryConfig(msg).label) }}
            <template v-if="msg.sent_at">{{
              `· ${formatDateTime(msg.sent_at)}`
            }}</template>
          </span>
          <span
            v-else-if="msg.status === 'failed' && msg.error"
            class="text-n-ruby-11 truncate max-w-full"
            :title="msg.error"
          >
            {{ msg.error }}
          </span>
        </div>

        <div
          v-if="msg.status === 'pending'"
          class="flex items-center gap-1 mt-1"
        >
          <Button
            xs
            slate
            faded
            :label="$t('SCHEDULED_MESSAGES.ACTIONS.EDIT')"
            icon="i-lucide-pencil"
            @click="emit('edit', msg)"
          />
          <Button
            xs
            ruby
            faded
            :label="$t('SCHEDULED_MESSAGES.ACTIONS.CANCEL')"
            icon="i-lucide-ban"
            @click="cancelMessage(msg)"
          />
        </div>

        <div
          v-if="msg.status === 'cancelled' || msg.status === 'failed'"
          class="flex items-center gap-1 mt-1"
        >
          <Button
            xs
            slate
            faded
            :label="$t('SCHEDULED_MESSAGES.ACTIONS.DELETE')"
            icon="i-lucide-trash-2"
            @click="deleteMessage(msg)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
